// Single source of truth for which permission(s) gate each Admin area — used by the sidebar
// nav filter, the route guard, and the tab list inside the Admin shell (views/admin/index.tsx).
// Tabs are matched by their `?tab=` query param rather than a fixed id, since the DB-seeded
// module-page PageKeys for these don't consistently match the static menu-items fallback (eg.
// the DB row keyed 'admin-site-settings' actually points at the "Sites" tab, not "Site Settings").

export const ADMIN_ROUTE = '/admin';

export interface AdminTabAccess {
  label: string;
  param: string;
  permissions: string[];
}

export const ADMIN_TABS: AdminTabAccess[] = [
  { label: 'Companies', param: 'companies', permissions: ['manage:companies', 'read:companies'] },
  // Sites is manage-only: read:sites alone (eg. for Site Settings) doesn't show this tab.
  { label: 'Sites', param: 'sites', permissions: ['manage:sites'] },
  { label: 'Devices', param: 'devices', permissions: ['manage:devices', 'read:devices'] },
  { label: 'Device Temperatures', param: 'device-temperatures', permissions: ['manage:temperatures', 'read:temperatures'] },
  { label: 'Attached Peripherals', param: 'peripherals', permissions: ['manage:peripherals', 'read:peripherals'] },
  { label: 'Site Settings', param: 'site-settings', permissions: ['manage:sites', 'read:sites'] },
  // Users is deliberately here rather than under Application Management: the page only ever shows
  // and edits users of the currently selected tenant, and manage:users/read:users are tenant-scoped
  // permissions held by tenant roles (Business Admin, Site Manager, User Admin, ...).
  { label: 'Users', param: 'users', permissions: ['manage:users', 'read:users'] },
  //{ label: 'Docket Config', param: 'docket-config', permissions: ['manage:application'] },
  //{ label: 'Docket Designer', param: 'docket-designer', permissions: ['manage:application'] },
  { label: 'Payment Types', param: 'payment-types', permissions: ['manage:payment_types', 'read:payment_types'] },
  { label: 'Report Schedules', param: 'report-schedules', permissions: ['edit:report_schedule', 'manage:report_schedules'] }
  //{ label: 'Error List', param: 'error-list', permissions: ['manage:application'] }
];

// Indices into ADMIN_TABS. Inserting a tab shifts everything after it, so these are the single
// place those positions are written down — nothing else should hardcode an index.
export const CompanyTab = 0;
export const SitesTab = 1;
export const DevicesTab = 2;
export const DeviceTemperaturesTab = 3;
export const AttachedPeripheralsTab = 4;
export const SiteSettingsTab = 5;
export const UsersTab = 6;
export const PaymentTypesTab = 7;
export const ReportSchedulesTab = 8;

export function adminTabUrl(param: string): string {
  return `${ADMIN_ROUTE}?tab=${param}`;
}

export function findAdminTabByUrl(url: string | undefined): AdminTabAccess | undefined {
  if (!url) return undefined;
  return ADMIN_TABS.find((tab) => url === adminTabUrl(tab.param));
}

export function canAccessAdminTab(hasPermission: (permissionCode: string) => boolean, tabIndex: number): boolean {
  const tab = ADMIN_TABS[tabIndex];
  if (!tab) return false;
  return tab.permissions.some((code) => hasPermission(code));
}

export function hasAnyAdminAccess(hasPermission: (permissionCode: string) => boolean): boolean {
  return ADMIN_TABS.some((tab) => tab.permissions.some((code) => hasPermission(code)));
}
